import { c as _c } from "react/compiler-runtime";
import type { GroupMentoringProductDTO, IndividualMentoringProductDTO, ProjectMentoringProductDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto';
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types';
import { cn } from '@algoacademy/design-system/shared/cn';
import { Badge } from '@algoacademy/design-system/web/data-display/Badge';
import { H3 } from '@algoacademy/design-system/web/marketing/typography/Heading/H3';
import { P3 } from '@algoacademy/design-system/web/marketing/typography/Paragraph';
import { calculateGroupMentoringPrice, calculateIndividualMentoringPrice, calculateMentoringSubscriptionPrice, calculateProjectMentoringPrice, MENTORING_SUBSCRIPTION_DISCOUNT_PERCENT, roundUpToNearestZloty } from './prices';
type BuyDataProps = {
  mentoringProduct: GroupMentoringProductDTO | IndividualMentoringProductDTO | ProjectMentoringProductDTO;
  goalPage?: boolean;
};
function computeBasePrice(mentoringProduct: BuyDataProps['mentoringProduct']): number {
  if (mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING) {
    return calculateIndividualMentoringPrice({
      hoursWithMentor: mentoringProduct.hoursWithMentor
    });
  }
  if (mentoringProduct.order === ORDER_TYPE.GROUP_MENTORING) {
    return calculateGroupMentoringPrice();
  }
  return calculateProjectMentoringPrice();
}
export function BuyData(t0) {
  const $ = _c(32);
  const {
    mentoringProduct,
    goalPage: t1
  } = t0;
  const goalPage = t1 === undefined ? false : t1;
  let discountPercent;
  let pricePerFourWeeks;
  let subscription;
  let t2;
  if ($[0] !== mentoringProduct) {
    pricePerFourWeeks = computeBasePrice(mentoringProduct);
    subscription = mentoringProduct.subscription;
    discountPercent = MENTORING_SUBSCRIPTION_DISCOUNT_PERCENT[subscription] ?? 0;
    t2 = calculateMentoringSubscriptionPrice(pricePerFourWeeks, subscription);
    $[0] = mentoringProduct;
    $[1] = discountPercent;
    $[2] = pricePerFourWeeks;
    $[3] = subscription;
    $[4] = t2;
  } else {
    discountPercent = $[1];
    pricePerFourWeeks = $[2];
    subscription = $[3];
    t2 = $[4];
  }
  const priceCalculation = t2;
  const {
    discountedPrice: discountedPriceForPeriod,
    savings: totalSavings,
    periodsCount
  } = priceCalculation;
  const originalPerFourWeeks = pricePerFourWeeks;
  let t3;
  if ($[5] !== discountPercent || $[6] !== pricePerFourWeeks) {
    t3 = discountPercent > 0 ? roundUpToNearestZloty(pricePerFourWeeks * (100 - discountPercent) / 100) : pricePerFourWeeks;
    $[5] = discountPercent;
    $[6] = pricePerFourWeeks;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const discountedPerFourWeeks = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <P3 className="text-center text-base text-contentPrimary">23% VAT w cenie</P3>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== discountPercent || $[10] !== originalPerFourWeeks) {
    t5 = discountPercent > 0 && <div className="flex flex-row items-center gap-2"><H3 className="line-through! relative font-normal! text-contentTertiary">{originalPerFourWeeks} zł</H3><Badge className="bg-blue-100 font-semibold text-algoprimary text-xs">-{discountPercent}%</Badge></div>;
    $[9] = discountPercent;
    $[10] = originalPerFourWeeks;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = goalPage && "text-gradient";
  let t7;
  if ($[12] !== t6) {
    t7 = cn("font-bold text-5xl lg:font-extrabold", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = discountPercent > 0 ? discountedPerFourWeeks : originalPerFourWeeks;
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <p className={t7}>{t8} zł</p>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <P3>/ 4 tygodnie</P3>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <div className="flex flex-row items-end gap-3">{t9}{t10}</div>;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== discountedPriceForPeriod || $[21] !== periodsCount || $[22] !== subscription) {
    t12 = periodsCount > 1 && <P3 className="text-contentTertiary">Przy rozliczeniu co {subscription.toLocaleLowerCase("pl")}:<br /> łącznie{" "}{discountedPriceForPeriod} zł</P3>;
    $[20] = discountedPriceForPeriod;
    $[21] = periodsCount;
    $[22] = subscription;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== periodsCount || $[25] !== totalSavings) {
    t13 = totalSavings > 0 && periodsCount > 1 && <p className="font-medium text-green-600 text-sm">Oszczędzasz {totalSavings} zł</p>;
    $[24] = periodsCount;
    $[25] = totalSavings;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t5) {
    t14 = <div className="space-y-3">{t4}<div className="flex flex-col items-center gap-2 text-center">{t5}{t11}{t12}{t13}</div></div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t5;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}